import type { BLOCK_TYPE, UNITS } from '../enums'

import type { IBlock, BlockBaseSettings, IBlockField } from './base'

export enum MeterReadingBlockMode {
  /**
   * Standard meter reading block
   * when the meter configuration is not known
   */
  STANDARD_INPUT = 'standard-input',
  /**
   * Meter reading block with entity updating capabilities
   * when meter configuration can be known from an existing entity in epilot
   */
  ENTITY_UPDATE = 'entity-update'
}

export enum CounterType {
  /**
   * A one-tariff counter records a single energy consumption value, regardless of the time of day.
   * This is a standard electricity meter where the rate remains the same at all times.
   */
  ONE_TARIFF = 'one-tariff',
  /**
   * A two-tariff counter (also called a dual-rate meter) records
   * energy consumption separately for two different time periods, typically:
   *   - Day (Peak) Usage: Higher rate during peak hours.
   *   - Night (Off-Peak) Usage: Lower rate during off-peak hours. This type of meter helps users benefit from lower night-time electricity prices.
   */
  TWO_TARIFF = 'two-tariff',
  /**
   * A bidirectional counter (also called a net meter) is used for energy systems
   * where electricity can both be consumed from the grid and fed back into it,
   * such as with solar panels. It records:
   *   - In: Energy consumed from the grid.
   *   - Out: Energy exported back to the grid.
   */
  BI_DIRECTIONAL = 'bi-directional'
}

type MeterReadingFields = {
  /**
   * Date of reading
   */
  date: IBlockField
  /**
   * Name/Role of the reader
   */
  by: IBlockField
  /**
   * Reason of reading
   */
  reason: IBlockField
}

type MeterConfiguration = {
  /**
   * A Market Location ID (MaLo ID) is a unique identifier for an energy
   * market location where electricity or gas is supplied.
   */
  marketLocationId: IBlockField
  /**
   * A meter ID is unique identifier for the physical meter deployed in a location
   */
  meterId: IBlockField
  /**
   * Choice of type of counters between one-tariff, two-tariff or bidirectional counter
   * This array configured the ability to choose from.
   *
   * @counterTypeChoices cannot be empty and must have atleast 1 choice.
   */
  counterTypeChoices: [CounterType, ...CounterType[]]
  /**
   * The meter's unit of measurement
   */
  unit: UNITS
}

export interface StandardMeterReadingBlock extends IBlock {
  type: BLOCK_TYPE.METER_READING
  settings: BlockBaseSettings & {
    mode: MeterReadingBlockMode.STANDARD_INPUT
    meter: MeterConfiguration
    fields: MeterReadingFields
  }
}

export interface EntityUpdatingMeterReadingBlock extends IBlock {
  type: BLOCK_TYPE.METER_READING
  settings: BlockBaseSettings & {
    mode: MeterReadingBlockMode.ENTITY_UPDATE
    fields: MeterReadingFields
  }
}

export type MeterReadingBlock =
  | StandardMeterReadingBlock
  | EntityUpdatingMeterReadingBlock
